import { ChangeEvent, useEffect, useState } from "react";
import moment from "moment";
import {
    Chart as ChartJS,
    Tooltip,
    Legend,
    ArcElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    BarElement,
} from 'chart.js';
import { Pie, Line, Bar } from 'react-chartjs-2';

import HeaderComponent from "../../components/header";
import ContainerComponent from "../../components/conatiner";
import CardHeaderBackgroundComponent from "../../components/cardHeaderBackground";
import CardInfoComponent from "../../components/cardInfo";
import InputComponent from "../../components/input";
import LoadingComponent from "../../components/loading";
import { ToastErro } from "../../components/toast";
import { FinancialDashboardService } from "../../services/financial/dashboard";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    ArcElement,
    Tooltip,
    Legend,
    BarElement,
);

const brl = (v: number) => (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const FinancialDashboardPage = () => {

    const [loading, setLoading] = useState<boolean>(true);
    const [startDate, setStartDate] = useState<string>(moment().startOf('month').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState<string>(moment().format('YYYY-MM-DD'));

    const [kpis, setKpis] = useState<any>({});
    const [agingPending, setAgingPending] = useState<any[]>([]);
    const [agingBuckets, setAgingBuckets] = useState<any>({});
    const [paymentsByMonth, setPaymentsByMonth] = useState<any[]>([]);
    const [spendingByCostCenter, setSpendingByCostCenter] = useState<any[]>([]);
    const [spendingByType, setSpendingByType] = useState<any[]>([]);
    const [topSpendingUsers, setTopSpendingUsers] = useState<any[]>([]);
    const [paymentMethod, setPaymentMethod] = useState<any[]>([]);
    const [spendingByBranch, setSpendingByBranch] = useState<any[]>([]);
    const [spendingByState, setSpendingByState] = useState<any[]>([]);
    const [pendingAdvancesDetail, setPendingAdvancesDetail] = useState<any[]>([]);

    const load = async () => {
        setLoading(true);
        try {
            const resp = await FinancialDashboardService(startDate, endDate);
            setKpis(resp.data.kpis || {});
            setAgingPending(resp.data.agingPending || []);
            setAgingBuckets(resp.data.agingBuckets || {});
            setPaymentsByMonth(resp.data.paymentsByMonth || []);
            setSpendingByCostCenter(resp.data.spendingByCostCenter || []);
            setSpendingByType(resp.data.spendingByType || []);
            setTopSpendingUsers(resp.data.topSpendingUsers || []);
            setPaymentMethod(resp.data.paymentMethod || []);
            setSpendingByBranch(resp.data.spendingByBranch || []);
            setSpendingByState(resp.data.spendingByState || []);
            setPendingAdvancesDetail(resp.data.pendingAdvancesDetail || []);
        } catch (error: any) {
            ToastErro(error?.response?.data?.message || 'Erro ao carregar dashboard');
        }
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const paymentsLineData = {
        labels: paymentsByMonth.map((m: any) => moment(m.month + '-01').format('MMM/YY')),
        datasets: [
            {
                label: 'Valor pago (R$)',
                data: paymentsByMonth.map((m: any) => Number(m.value)),
                borderColor: '#008ffb',
                backgroundColor: 'rgba(0,143,251,0.15)',
                yAxisID: 'y',
                tension: 0.3,
            },
            {
                label: 'Qtd. ordens',
                data: paymentsByMonth.map((m: any) => Number(m.orders_count)),
                borderColor: '#00e396',
                backgroundColor: 'rgba(0,227,150,0.15)',
                yAxisID: 'y1',
                tension: 0.3,
            }
        ]
    };

    const ccBarData = {
        labels: spendingByCostCenter.map((c: any) => c.cost_center),
        datasets: [{
            label: 'Valor (R$)',
            data: spendingByCostCenter.map((c: any) => Number(c.value)),
            backgroundColor: '#008ffb',
        }]
    };

    const typePieData = {
        labels: spendingByType.map((t: any) => t.type),
        datasets: [{
            data: spendingByType.map((t: any) => Number(t.value)),
            backgroundColor: spendingByType.map((t: any) => t.color || '#cccccc'),
        }]
    };

    const agingBucketsData = {
        labels: ['< 1 dia', '1-3 dias', '4-7 dias', '8-15 dias', '> 15 dias'],
        datasets: [{
            label: 'Ordens aguardando',
            data: [
                Number(agingBuckets.up_to_1 || 0),
                Number(agingBuckets.d1_3 || 0),
                Number(agingBuckets.d4_7 || 0),
                Number(agingBuckets.d8_15 || 0),
                Number(agingBuckets.d15_plus || 0),
            ],
            backgroundColor: ['#00e396', '#feb019', '#ff9800', '#ff4560', '#775dd0'],
        }]
    };

    const paymentMethodPieData = {
        labels: paymentMethod.map((p: any) => Number(p.payment_business_card) === 1 ? 'Cartão Empresarial' : 'Depósito / Outros'),
        datasets: [{
            data: paymentMethod.map((p: any) => Number(p.value)),
            backgroundColor: ['#008ffb', '#00e396'],
        }]
    };

    // agrupa top 7 + "Outros" para um donut legível mesmo com muitas filiais
    const branchTop = spendingByBranch.slice(0, 7);
    const branchOthers = spendingByBranch.slice(7);
    const branchOthersSum = branchOthers.reduce((acc: number, b: any) => acc + Number(b.value), 0);
    const branchDonutLabels = [
        ...branchTop.map((b: any) => b.branch || 'Sem filial'),
        ...(branchOthers.length > 0 ? [`Outros (${branchOthers.length})`] : [])
    ];
    const branchDonutValues = [
        ...branchTop.map((b: any) => Number(b.value)),
        ...(branchOthers.length > 0 ? [branchOthersSum] : [])
    ];
    const branchPalette = ['#008ffb', '#00e396', '#feb019', '#ff4560', '#775dd0', '#00bcd4', '#ff9800', '#9e9e9e'];
    const branchDonutData = {
        labels: branchDonutLabels,
        datasets: [{ data: branchDonutValues, backgroundColor: branchPalette }]
    };

    const branchTotal = spendingByBranch.reduce((acc: number, b: any) => acc + Number(b.value), 0);

    const stateBarData = {
        labels: spendingByState.map((s: any) => `${s.state} (${s.uf})`),
        datasets: [{
            label: 'Valor (R$)',
            data: spendingByState.map((s: any) => Number(s.value)),
            backgroundColor: '#feb019',
        }]
    };

    const renderVariation = (pct: number | null) => {
        if (pct === null || pct === undefined) return <span className="text-muted" style={{ fontSize: 12 }}>sem período anterior</span>;
        const up = pct >= 0;
        return (
            <span style={{ fontSize: 12, color: up ? '#00a76a' : '#d63e3e', fontWeight: 600 }}>
                <i className="material-icons" style={{ fontSize: 14, verticalAlign: 'middle' }}>{up ? 'arrow_upward' : 'arrow_downward'}</i>
                {Math.abs(pct).toFixed(1)}% vs período anterior
            </span>
        );
    };

    return (
        <>
            <HeaderComponent title="Dashboard Financeiro" />
            <ContainerComponent>
                <>
                    <CardHeaderBackgroundComponent title="Período">
                        <div className="row align-items-end">
                            <InputComponent
                                label="Data Inicial"
                                col={3}
                                type="date"
                                defaultValue={startDate}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setStartDate(e.target.value)}
                                size="small"
                                shrink
                            />
                            <InputComponent
                                label="Data Final"
                                col={3}
                                type="date"
                                defaultValue={endDate}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setEndDate(e.target.value)}
                                size="small"
                                shrink
                            />
                            <div className="col-md-3 mb-4">
                                <button className="btn btn-primary" type="button" onClick={load}>Atualizar</button>
                            </div>
                        </div>
                    </CardHeaderBackgroundComponent>

                    {loading ? <LoadingComponent /> : (
                        <>
                            {/* KPIs */}
                            <div className="row">
                                <CardInfoComponent col={3} icon="hourglass_empty" title="Pendentes de pagamento" subtitle={String(kpis.pending_orders_count ?? 0)} />
                                <CardInfoComponent col={3} icon="payments" title="Valor pendente" subtitle={brl(kpis.pending_orders_value)} />
                                <CardInfoComponent col={3} icon="check_circle" title="Pagas no período" subtitle={String(kpis.paid_orders_in_range ?? 0)} />
                                <CardInfoComponent col={3} icon="attach_money" title="Valor pago no período" subtitle={brl(kpis.paid_value_in_range)} />
                            </div>
                            <div className="row px-2 mb-4">
                                <div className="col-md-6 mb-2">{renderVariation(kpis.variation_count_pct)} <span className="text-muted" style={{ fontSize: 12 }}>— ordens pagas</span></div>
                                <div className="col-md-6 mb-2">{renderVariation(kpis.variation_value_pct)} <span className="text-muted" style={{ fontSize: 12 }}>— valor pago</span></div>
                            </div>
                            <div className="row">
                                <CardInfoComponent col={3} icon="receipt_long" title="Ticket médio por ordem" subtitle={brl(kpis.avg_ticket)} />
                                <CardInfoComponent col={3} icon="sync" title="Ciclo total (cadastro→pago, dias)" subtitle={kpis.avg_cycle_total_days !== null ? String(kpis.avg_cycle_total_days) : '-'} />
                                <CardInfoComponent col={3} icon="timer" title="Ciclo financeiro (envio→pago, dias)" subtitle={kpis.avg_days_to_pay !== null ? String(kpis.avg_days_to_pay) : '-'} />
                                <CardInfoComponent col={3} icon="warning" title="Mais antiga pendente (dias)" subtitle={kpis.oldest_pending_days !== null ? String(kpis.oldest_pending_days) : '-'} />
                            </div>
                            <div className="row">
                                <CardInfoComponent col={6} icon="account_balance_wallet" title="Adiantamentos pendentes (qtd)" subtitle={String(kpis.pending_advances_count ?? 0)} />
                                <CardInfoComponent col={6} icon="savings" title="Valor de adiantamentos pendentes" subtitle={brl(kpis.pending_advances_value)} />
                            </div>

                            {/* Evolução mensal + aging buckets */}
                            <div className="row gx-5">
                                <div className="col-xl-8 col-lg-8 mb-5">
                                    <div className="card card-raised h-100">
                                        <div className="card-header bg-primary text-white px-4"><div className="fw-500">Pagamentos — últimos 12 meses</div></div>
                                        <div className="card-body" style={{ position: 'relative', height: 340 }}>
                                            <Line
                                                data={paymentsLineData}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    scales: {
                                                        y: { position: 'left', title: { display: true, text: 'Valor (R$)' } },
                                                        y1: { position: 'right', title: { display: true, text: 'Qtd.' }, grid: { drawOnChartArea: false } }
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-4 col-lg-4 mb-5">
                                    <div className="card card-raised h-100">
                                        <div className="card-header bg-primary text-white px-4"><div className="fw-500">Distribuição de aging (pendentes)</div></div>
                                        <div className="card-body" style={{ position: 'relative', height: 340 }}>
                                            <Bar
                                                data={agingBucketsData}
                                                options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Gastos por CC + categoria */}
                            <div className="row gx-5">
                                <div className="col-xl-8 col-lg-8 mb-5">
                                    <div className="card card-raised h-100">
                                        <div className="card-header bg-primary text-white px-4"><div className="fw-500">Gastos por centro de custo (período)</div></div>
                                        <div className="card-body" style={{ position: 'relative', height: 340 }}>
                                            <Bar
                                                data={ccBarData}
                                                options={{ indexAxis: 'y' as const, responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-4 col-lg-4 mb-5">
                                    <div className="card card-raised h-100">
                                        <div className="card-header bg-primary text-white px-4"><div className="fw-500">Gastos por categoria</div></div>
                                        <div className="card-body" style={{ position: 'relative', height: 340 }}>
                                            {spendingByType.length > 0
                                                ? <Pie data={typePieData} options={{ responsive: true, maintainAspectRatio: false }} />
                                                : <div className="text-center text-muted mt-4">Sem dados no período</div>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Cartão empresarial + Filial */}
                            <div className="row gx-5">
                                <div className="col-xl-4 col-lg-4 mb-5">
                                    <div className="card card-raised h-100">
                                        <div className="card-header bg-primary text-white px-4"><div className="fw-500">Cartão Empresarial × Depósito</div></div>
                                        <div className="card-body">
                                            <div style={{ position: 'relative', height: 240 }}>
                                                {paymentMethod.length > 0
                                                    ? <Pie data={paymentMethodPieData} options={{ responsive: true, maintainAspectRatio: false }} />
                                                    : <div className="text-center text-muted mt-4">Sem dados no período</div>
                                                }
                                            </div>
                                            <div className="mt-3">
                                                {paymentMethod.map((p: any, i: number) => (
                                                    <div key={i} className="d-flex justify-content-between" style={{ fontSize: 13 }}>
                                                        <span>{Number(p.payment_business_card) === 1 ? 'Cartão Empresarial' : 'Depósito / Outros'}</span>
                                                        <span><b>{brl(Number(p.value))}</b> <span className="text-muted">({p.orders_count} ordens)</span></span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-8 col-lg-8 mb-5">
                                    <div className="card card-raised h-100">
                                        <div className="card-header bg-primary text-white px-4"><div className="fw-500">Gastos por filial (período)</div></div>
                                        <div className="card-body">
                                            {spendingByBranch.length > 0 ? (
                                                <div className="row">
                                                    <div className="col-md-5" style={{ position: 'relative', height: 280 }}>
                                                        <Pie
                                                            data={branchDonutData}
                                                            options={{
                                                                responsive: true,
                                                                maintainAspectRatio: false,
                                                                plugins: { legend: { display: false } },
                                                                cutout: '55%'
                                                            } as any}
                                                        />
                                                    </div>
                                                    <div className="col-md-7">
                                                        <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                                                            <table className="table table-sm mb-0">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Filial</th>
                                                                        <th>Ordens</th>
                                                                        <th className="text-end">Valor</th>
                                                                        <th className="text-end">%</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {spendingByBranch.map((b: any, i: number) => {
                                                                        const pct = branchTotal > 0 ? (Number(b.value) / branchTotal) * 100 : 0;
                                                                        const color = branchPalette[Math.min(i, branchPalette.length - 1)];
                                                                        return (
                                                                            <tr key={i}>
                                                                                <td>
                                                                                    <span style={{ display: 'inline-block', width: 10, height: 10, backgroundColor: color, borderRadius: 2, marginRight: 6 }}></span>
                                                                                    {b.branch || 'Sem filial'}
                                                                                </td>
                                                                                <td>{b.orders_count}</td>
                                                                                <td className="text-end">{brl(Number(b.value))}</td>
                                                                                <td className="text-end">{pct.toFixed(1)}%</td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : <div className="text-center text-muted mt-4">Sem dados no período</div>}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Estados + Adiantamentos pendentes detalhados */}
                            <div className="row gx-5">
                                <div className="col-xl-6 col-lg-6 mb-5">
                                    <div className="card card-raised h-100">
                                        <div className="card-header bg-primary text-white px-4"><div className="fw-500">Gastos por estado (top 15)</div></div>
                                        <div className="card-body" style={{ position: 'relative', height: 340 }}>
                                            {spendingByState.length > 0
                                                ? <Bar data={stateBarData} options={{ indexAxis: 'y' as const, responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
                                                : <div className="text-center text-muted mt-4">Sem dados no período</div>
                                            }
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-6 col-lg-6 mb-5">
                                    <div className="card card-raised h-100">
                                        <div className="card-header bg-primary text-white px-4"><div className="fw-500">Adiantamentos pendentes — quem deve</div></div>
                                        <div className="card-body">
                                            <div className="table-responsive">
                                                <table className="table table-sm">
                                                    <thead>
                                                        <tr>
                                                            <th>Usuário</th>
                                                            <th>Lançado por</th>
                                                            <th>Desde</th>
                                                            <th>Dias</th>
                                                            <th className="text-end">Valor</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {pendingAdvancesDetail.map((a: any) => (
                                                            <tr key={a.id}>
                                                                <td>{a.user_name}</td>
                                                                <td>{a.financial_name || '-'}</td>
                                                                <td>{a.created_at ? moment(a.created_at).format('DD/MM/YYYY') : '-'}</td>
                                                                <td>{a.days_open !== null ? Number(a.days_open).toFixed(0) : '-'}</td>
                                                                <td className="text-end">{brl(Number(a.value))}</td>
                                                            </tr>
                                                        ))}
                                                        {pendingAdvancesDetail.length === 0 && (
                                                            <tr><td colSpan={5} className="text-center text-muted">Nenhum adiantamento pendente</td></tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Aging de pendentes + top usuários */}
                            <div className="row gx-5">
                                <div className="col-xl-7 col-lg-7 mb-5">
                                    <div className="card card-raised h-100">
                                        <div className="card-header bg-primary text-white px-4"><div className="fw-500">Top 10 ordens aguardando pagamento (mais antigas)</div></div>
                                        <div className="card-body">
                                            <div className="table-responsive">
                                                <table className="table table-sm">
                                                    <thead>
                                                        <tr>
                                                            <th>Nº</th>
                                                            <th>Usuário</th>
                                                            <th>Centro de Custo</th>
                                                            <th>Enviado em</th>
                                                            <th>Dias</th>
                                                            <th className="text-end">Valor</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {agingPending.map((o: any) => (
                                                            <tr key={o.id}>
                                                                <td>{o.id}</td>
                                                                <td>{o.name}</td>
                                                                <td>{o.cost_center}</td>
                                                                <td>{o.sent_to_financial_at ? moment(o.sent_to_financial_at).format('DD/MM/YYYY HH:mm') : '-'}</td>
                                                                <td>{o.days_waiting !== null ? Number(o.days_waiting).toFixed(1) : '-'}</td>
                                                                <td className="text-end">{brl(Number(o.value))}</td>
                                                            </tr>
                                                        ))}
                                                        {agingPending.length === 0 && (
                                                            <tr><td colSpan={6} className="text-center text-muted">Nenhuma ordem pendente</td></tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-5 col-lg-5 mb-5">
                                    <div className="card card-raised h-100">
                                        <div className="card-header bg-primary text-white px-4"><div className="fw-500">Top 10 usuários por valor (período)</div></div>
                                        <div className="card-body">
                                            <div className="table-responsive">
                                                <table className="table table-sm">
                                                    <thead>
                                                        <tr>
                                                            <th>Usuário</th>
                                                            <th>Ordens</th>
                                                            <th className="text-end">Valor</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {topSpendingUsers.map((u: any, i: number) => (
                                                            <tr key={i}>
                                                                <td>{u.name}</td>
                                                                <td>{u.orders_count}</td>
                                                                <td className="text-end">{brl(Number(u.value))}</td>
                                                            </tr>
                                                        ))}
                                                        {topSpendingUsers.length === 0 && (
                                                            <tr><td colSpan={3} className="text-center text-muted">Sem dados no período</td></tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </>
            </ContainerComponent>
        </>
    );
};

export default FinancialDashboardPage;
